// React
import { JSX } from 'react';

// Third-party
import Skeleton from 'react-loading-skeleton';

// Components
import { MlCard, MlCopyBlock } from '@/components/molecules';
import {
  OrContentBlock,
  OrStatsList,
  isIconCardItem,
  isListItem,
} from '@/components/organisms';
import {
  EStatsListVariants,
  TStat,
} from '@/components/organisms/or-stats-list/or-stats-list.types';

// Helpers
import RenderComponents from '@/components/helpers/cms/RenderComponents';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';

// Types
import { EBlockAlignment, EThemes } from '@/lib/contentstack-sdk/types';

// Utils
import { ITmHomePlanDetailProps } from './tm-home-plan-detail.types';
import { TmHomePlanHeroClient } from './tm-home-plan-detail-hero.client';
import { TmHomePlanDetailFloorplansClient } from './tm-home-plan-detail-floorplans.client';
import { TmHomePlanDetailDesignStudioClient } from './tm-home-plan-detail-design-studio.client';
import { TmHomePlanDetailThreeDBanner } from './tm-home-plan-detail-three-d-banner.client';
import { TmHomePlanDetailWhatsIncludedCta } from './tm-home-plan-detail-whats-included-cta.client';

export const TmHomePlanDetail = ({
  block,
  baseElevation,
}: ITmHomePlanDetailProps): JSX.Element => {
  const {
    home_plan_name,
    home_plan_hero_section,
    home_plan_details,
    home_plan_id,
    model_home_details,
    body,
    whats_included_section,
    has_three_d,
  } = block;

  const planId = Number(home_plan_id.select);

  const buildStatsList = (): TStat[] => [
    {
      subtitle: 'Beds',
      title: baseElevation?.Plan?.Bedrooms?.toString() || '',
    },
    {
      subtitle: 'Baths',
      title: baseElevation?.Plan?.Bathrooms?.toString() || '',
    },
    {
      subtitle: 'Square Feet',
      title: baseElevation?.SquareFeet?.toString() || '',
    },
    {
      subtitle: 'Footprint (feet)',
      title: `${baseElevation?.Width} x ${baseElevation?.Depth}`,
    },
    {
      subtitle: 'Garage',
      title: baseElevation?.Plan?.HasGarage ? 'Included' : 'Not Included',
    },
  ];

  const RenderLeftContentWhatsIncluded = () => {
    const left = whats_included_section?.[0].left_content?.[0];
    return (
      <MlCopyBlock
        title={left?.title || "What's Included?"}
        titleElement={left?.heading_element?.select || 'h2'}
        titleVariant={left?.heading_variant?.select || 'h2'}
        alignment={
          left?.alignment?.alignmentprop
            ? EBlockAlignment[left.alignment.alignmentprop]
            : EBlockAlignment.LEFT
        }
      >
        <RenderRichText body={left?.description} />
        <TmHomePlanDetailWhatsIncludedCta leftContent={left} />
      </MlCopyBlock>
    );
  };

  const heroTooltipBody = home_plan_hero_section.find((item) => item.tooltip)
    ?.tooltip?.body;
  const autoplay = home_plan_hero_section.find((item) => item?.autoplay)
    ?.autoplay?.autoplay;

  return (
    <div>
      {false ? (
        <div className="container">
          <Skeleton
            baseColor="var(--color-neutral-100)"
            highlightColor="var(--color-neutral-200)"
            height={200}
            className="rounded-30"
          />
        </div>
      ) : (
        <>
          <TmHomePlanHeroClient
            homePlanName={home_plan_name}
            planId={planId}
            heroSection={home_plan_hero_section}
            heroTooltipBody={heroTooltipBody}
            autoplay={autoplay}
            hasThreeD={has_three_d}
          />
          {baseElevation && (
            <OrStatsList
              variant={EStatsListVariants.HOME_PLAN}
              stats={buildStatsList()}
            />
          )}
          <TmHomePlanDetailFloorplansClient
            planId={planId}
            homePlanDetails={home_plan_details}
          />

          <TmHomePlanDetailDesignStudioClient
            modelHomeDetails={model_home_details}
          />

          {whats_included_section && whats_included_section.length > 0 && (
            <OrContentBlock
              topSpacing={
                whats_included_section?.[0]?.top_spacing?.spacing_value ?? 0
              }
              bottomSpacing={
                whats_included_section?.[0]?.bottom_spacing?.spacing_value ?? 0
              }
              topDivider={whats_included_section?.[0]?.top_spacing?.divider}
              bottomDivider={
                whats_included_section?.[0]?.bottom_spacing?.divider
              }
              RenderLeftContent={RenderLeftContentWhatsIncluded}
              theme={EThemes[whats_included_section[0]?.theme?.theme]}
            >
              {whats_included_section?.[0].right_content.map((contents) => {
                if (isIconCardItem(contents)) {
                  const { description = '', title, icon } = contents.icon_card;
                  return (
                    <MlCard
                      key={`${contents.icon_card?._metadata?.uid} - ${title} `}
                      iconName={icon}
                      theme={EThemes[whats_included_section[0].theme.theme]}
                      title={title}
                      body={description}
                      headerElement="h3"
                      className="[&_p]:paragraph-base rounded-none !px-0 [&_h3]:font-Figtree [&_p]:text-22 [&_p]:lg:text-2xl"
                    />
                  );
                }

                if (isListItem(contents)) {
                  const {
                    content,
                    _metadata: { uid },
                  } = contents.list;
                  return (
                    <RenderRichText
                      body={content}
                      key={uid}
                      className="prose-li:text-large prose prose-li:pb-6 prose-li:text-lg prose-li:text-[var(--color-text-secondary)] lg:prose-li:text-2xl"
                    />
                  );
                }

                return null;
              })}
            </OrContentBlock>
          )}

          <TmHomePlanDetailThreeDBanner
            planId={planId}
            hasThreeD={has_three_d}
          />

          {body && body.length > 0 && (
            <RenderComponents isPage={false} body={body} />
          )}
        </>
      )}
    </div>
  );
};
